/**
 * Created by jonas on 2021-02-09.
 */
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {Coordinate2d, SimpleReelModelSymbolData} from "../../core/reel/simplereel/SimpleReelModel";
import {BS2Statics} from "../config/BS2Statics";
import {BS2GameAssets} from "../BS2GameAssets";
import {gsap} from "gsap";
import {BS2Reel} from "./BS2Reel";
import {BS2} from "../BS2";
import {StateMatrix} from "../utils/StateMatrix";
import {MathUtils} from "../utils/MathUtils";
import {SpineGSAPWrapper} from "../animation/SpineGSAPWrapper";
import {FreespinCounterView} from "../view/FreespinCounterView";
import {RitualCollectorBar} from "../view/parts/RitualCollectorBar";
import {ScatterSymbol} from "./ScatterSymbol";
import {LayersSet} from "./LayersSet";
import {BS2BottomStickyWildViewController} from "../view/BS2StickyWildLockViewController";
import {BS2AddTextToProgressionBarView} from "../view/BS2AddTextToProgressionBarView";
import {BS2GameMode} from "../server/BS2GameMode";


export class BS2ReelsView extends PIXI.Container implements StageLayer {
    public reels: BS2Reel[];
    public stickySymbols: StateMatrix<SimpleReelModelSymbolData | null>;
    public freespinCounter: FreespinCounterView;
    public collectBar: RitualCollectorBar;
    public addTextToProgressionBar: BS2AddTextToProgressionBarView;
    public bottomStickyWildView: BS2BottomStickyWildViewController;
    private _layers: LayersSet;
    private _reelFrame: PIXI.spine.Spine;
    private _maskGfx: PIXI.Graphics;
    private _maskGfxForSplit: PIXI.Graphics;
    private _squeezedScatters: ScatterSymbol[] = [];
    private _currentLevel: number = 0;

    constructor() {
        super();
        this.name = "BS2ReelsView";
        this.init();
    }

    onResize(): void {
        const isSsfsPosition: boolean = BS2.model.data.mode === BS2GameMode.FREESPIN_SUPER || BS2.model.data.nextMode === BS2GameMode.FREESPIN_SUPER || BS2.model.data.mode === BS2GameMode.AVALANCHE_FREESPIN_SUPER || BS2.model.data.nextMode === BS2GameMode.AVALANCHE_FREESPIN_SUPER;
        this.position.set(0,isSsfsPosition?34: BS2Statics.getShortScreenOffset());
    }

    public initReels(reelsData: string[][], reelHeight: number, isAvalancheRestore: boolean = true) {
        for (let i: number = 0; i < this.reels.length; i++) {
            this.reels[i].init(reelsData[i], reelHeight, isAvalancheRestore);
            this.reels[i].model.active = true;
            this.reels[i].model.collectable = true;
        }
    }

    getAllVisibleSymbols(): SimpleReelModelSymbolData[] {
        const result: SimpleReelModelSymbolData[] = [];
        for (let i: number = 0; i < this.reels.length; i++) {
            result.push(...this.reels[i].model.getAllVisibleSymbols());
        }
        return result;
    }

    getVisibleSymbolDataAt(column: number, row: number): SimpleReelModelSymbolData {
        return this.reels[column].model.getVisibleSymbolAt(row);
    }

    public getBounceShake(): gsap.core.Timeline {
        const tl = gsap.timeline();

        tl.add(gsap.to(this, {duration: 0.05, x: -4, y: 3}));
        tl.add(gsap.to(this, {duration: 0.05, x: 0, y: 0}));

        return tl;
    }

    setReeSet(reelSet: string[][]) {
        for (let i: number = 0; i < this.reels.length; i++) {
            this.reels[i].model.setReelSet(reelSet[i]);
        }
    }

    addSqueezedScatter(scatterSymbol: ScatterSymbol) {
        this._squeezedScatters.push(scatterSymbol);
    }

    public hideAllRemainingScatters() {
        while (this._squeezedScatters.length > 0) {
            const scatter: ScatterSymbol | undefined = this._squeezedScatters.pop();
            if (scatter != undefined) {
                const tl: gsap.core.Timeline = gsap.timeline();
                tl.add(gsap.to(scatter, {duration: 0.1, alpha: 0, ease: "none"}));
                tl.add(() => {
                    scatter.destroy();
                })
            }
        }
    }

    public switchLevel(targetLevel: number, includeStart: boolean = true): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        if (this._currentLevel === targetLevel) {
            return tl;
        }
        targetLevel = MathUtils.constrain(targetLevel, 0, 5);
        if (this._currentLevel < targetLevel && targetLevel !== 0) {
            const newLevelStartTl: gsap.core.Timeline = gsap.timeline();
            newLevelStartTl.add(SpineGSAPWrapper.createTimeline(this._reelFrame, 0, "level" + targetLevel, 0));
            if (targetLevel === 5) {
                newLevelStartTl.add([
                    SpineGSAPWrapper.createTimeline(this._reelFrame, 0, "ProgressBarBreak", 0),
                    BS2.reelView.collectBar.hide(0.3)
                ], "<");
            }
            tl.add(newLevelStartTl);
        }
        if (targetLevel === 0) {
            if (this._currentLevel === 5) {
                tl.add(SpineGSAPWrapper.createTimeline(this._reelFrame, 0, "ProgressBarToBasegame", 0));
                tl.add(() => {
                    BS2.reelView.collectBar.show(0.1);
                }, ">-0.4");
            }
            tl.add(SpineGSAPWrapper.createTimeline(this._reelFrame, 0, "normal", 0));
        }

        //only for handling #128
        if (targetLevel >0 && targetLevel <5 && this._currentLevel === 5) {
            tl.add(SpineGSAPWrapper.createTimeline(this._reelFrame, 0, "ProgressBarToBasegame", 0));
            tl.add(() => {
                BS2.reelView.collectBar.show(0.1);
            }, ">-0.4");
            tl.add(SpineGSAPWrapper.createTimeline(this._reelFrame, 0, "level" + targetLevel, 0));
        }

        this._currentLevel = targetLevel;
        return tl;
    }


    private init() {

        this._layers = new LayersSet();
        this.bottomStickyWildView = new BS2BottomStickyWildViewController();

        this._reelFrame = new PIXI.spine.Spine(BS2.assetLoader.getSpineAsset(BS2GameAssets.REEL_FRAME_SPINE));
        this._reelFrame.name = "_reelFrame";
        this._reelFrame.position.set(360, 338);

        this.freespinCounter = new FreespinCounterView();

        this.collectBar = new RitualCollectorBar();


        const _startPositions: Coordinate2d[] = [];
        for (let i: number = 0; i < BS2Statics.NR_OF_REELS; i++) {
            _startPositions.push({
                x: i * BS2Statics.REEL_X_OFFSET + BS2Statics.REEL_AREA_X_OFFSET,
                y: BS2Statics.REEL_AREA_Y_OFFSET
            });
        }

        this.reels = [];
        let reelId: number = 0;
        for (let i: number = 0; i < _startPositions.length; i++) {
            const pos = _startPositions[i];
            const reel: BS2Reel = new BS2Reel(reelId)
            reelId++;
            reel.position.set(pos.x, pos.y);
            this.reels.push(reel);
        }

        for (let i: number = this.reels.length - 1; i >= 0; i--) {
            this._layers.addLayerSet(this.reels[i].layers);
        }


        this.stickySymbols = new StateMatrix<SimpleReelModelSymbolData | null>();
        this.stickySymbols.initWithValue(5, 5, null);

        this.addTextToProgressionBar = new BS2AddTextToProgressionBarView();


        this._maskGfx = new PIXI.Graphics();
        this._maskGfx.beginFill(0, 1);
        this._maskGfx.drawRect(0, 0, 900, 5 * BS2Statics.SYMBOL_HEIGHT + 7)
        this._maskGfx.position.set(-90, BS2Statics.REEL_AREA_Y_OFFSET - BS2Statics.SYMBOL_HEIGHT * 0.5);
        this._maskGfx.endFill();

        this._maskGfxForSplit = new PIXI.Graphics();
        this._maskGfxForSplit.beginFill(0, 1);
        this._maskGfxForSplit.drawRect(0, 0, 5 * BS2Statics.SYMBOL_WIDTH, 5 * BS2Statics.SYMBOL_HEIGHT);
        this._maskGfxForSplit.name = "maskGfxForSplit"
        this._maskGfxForSplit.position.set(360, 319);
        this._maskGfxForSplit.pivot.set(5 * BS2Statics.SYMBOL_WIDTH / 2, 5 * BS2Statics.SYMBOL_HEIGHT / 2);
        this._maskGfxForSplit.endFill();


        this.addChild(this._maskGfx, this._maskGfxForSplit);
        this.addChild(this._layers.maskedNormal);
        this.addChild(this._layers.wildLocked);
        this.addChild(this._reelFrame);
        this.addChild(this.freespinCounter);
        this.addChild(this._layers.maskedHighlight);
        this.addChild(this._layers.win);
        this.addChild(this._layers.winOver);
        this.addChild(this._layers.overlay);
        this.addChild(this._layers.splitOverlay);
        this.addChild(this.bottomStickyWildView);
        this.addChild(this.collectBar);
        this.addChild(this.addTextToProgressionBar);

        this._layers.maskedNormal.mask = this._maskGfx;
        this._layers.wildLocked.mask = this._maskGfx;
        this._layers.maskedHighlight.mask = this._maskGfx;
        this._layers.splitOverlay.mask = this._maskGfxForSplit;

    }

}